/**
 * My Calendar — single-person month grid with paint mode + shortcuts +
 * busy indicator. The user's primary editing surface.
 */
import { useState, useEffect, useRef } from "preact/hooks";
import type { JSX } from "preact";
import {
  state,
  currentYear,
  currentMonth,
  paintCode,
  editingTarget,
  setCalendarCell,
} from "../state/store";
import { dateKey, daysInMonth, isWeekend } from "../utils/dates";
import { getHoliday } from "../utils/holidays";
import { displayName, activePeople } from "../utils/names";
import { busynessClass } from "../utils/busyness";
import { toast } from "../utils/toast";
import { STATUS_LABEL, type StatusCode } from "../types";
import { MONTHS } from "../config";
import { DayModal } from "./DayModal";

// Order kept in sync with dashboardMetrics.STATUS_CODES and DayModal so
// the palette / legend / dashboard / Excel export all use the same
// visual ordering. V is grouped with the other physical-office codes
// (P, M, V) at the start; O (out of office) stays last.
const STATUS_CODES: StatusCode[] = ["P", "T", "R", "M", "V", "A", "O"];

export function MyCalendar(): JSX.Element {
  const [modalDate, setModalDate] = useState<string | null>(null);
  const dayListRef = useRef<HTMLUListElement | null>(null);
  const s = state.value;
  const me = editingTarget.value;
  const y = currentYear.value;
  const m = currentMonth.value;
  const nDays = daysInMonth(y, m);
  const firstWd = new Date(y, m, 1).getDay();

  const busyByDay: number[] = new Array(nDays + 1).fill(0);
  Object.keys(s.roster)
    .filter((n) => s.roster[n]?.active !== false)
    .forEach((name) => {
      const cal = s.calendar[name];
      if (!cal) return;
      for (let d = 1; d <= nDays; d++) {
        const c = cal[dateKey(y, m, d)];
        // All physical-office statuses contribute to the busy indicator:
        // P (Lodi), M (Milan), V (Rovereto). Keeping this list in sync
        // with IN_OFFICE_CODES in dashboardMetrics is what makes the
        // calendar's busy bar match the Dashboard's "in office" numbers.
        if (c === "P" || c === "M" || c === "V") busyByDay[d]!++;
      }
    });

  const totalPeople = activePeople(s.roster).length;
  const today = new Date();

  // Per-category totals for the visible person + visible month — feeds the
  // "Month summary" card in the sidebar so the user can see their
  // P/T/R/M/O/A mix at a glance without scanning the grid.
  const monthCounts: Record<StatusCode, number> = { P: 0, T: 0, R: 0, M: 0, O: 0, A: 0, V: 0 };
  if (me) {
    const myCal = s.calendar[me] ?? {};
    for (let d = 1; d <= nDays; d++) {
      const c = myCal[dateKey(y, m, d)];
      if (c && c in monthCounts) monthCounts[c as StatusCode]++;
    }
  }
  const monthFilled = STATUS_CODES.reduce((acc, c) => acc + monthCounts[c], 0);

  // ---- Drag-to-paint --------------------------------------------------------
  // Click-and-drag across days to apply the active paint code in bulk.
  // Mode is decided on mousedown:
  //   - clicking a cell that already has paintCode  → drag erases
  //   - clicking any other cell                     → drag paints
  // A single click without movement still works because we apply the
  // mutation eagerly on mousedown; mouseup is just the "release" event.
  const dragModeRef = useRef<"paint" | "erase" | null>(null);
  const [, forceRender] = useState(0);

  useEffect(() => {
    const onUp = (): void => {
      if (dragModeRef.current !== null) {
        dragModeRef.current = null;
        forceRender((n) => n + 1); // refresh cursor styling on the body
      }
    };
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
  }, []);

  // Mobile day-list: land on today's row when the visible month is the
  // current month (the list is the rendered surface only at ≤720px — guard
  // on its computed display so this is a no-op on the desktop grid). Reads
  // only local primitives (y / m) — no signal in deps.
  useEffect(() => {
    const list = dayListRef.current;
    if (!list || getComputedStyle(list).display === "none") return;
    const now = new Date();
    if (now.getFullYear() !== y || now.getMonth() !== m) return;
    const li = list.children[now.getDate() - 1] as HTMLElement | undefined;
    li?.scrollIntoView({ block: "center" });
  }, [y, m]);

  // Plain admins (admin but not super admin) whose email isn't on the
  // roster have no calendar to edit — show a clear empty state instead
  // of letting the rest of the view try to dereference `me` as null.
  // Super admins always have at least one default actAs target (the
  // first active person), so this branch fires only for the rare
  // unlinked-admin case. Placed AFTER all hooks so the early return
  // doesn't break React's rules-of-hooks.
  if (!me) {
    return (
      <div class="my-layout">
        <aside class="my-side">
          <div class="card">
            <h3>No personal calendar</h3>
            <p class="hint">
              Your account isn't linked to a roster row, so there's no
              personal calendar to fill in here. Use the <strong>Dashboard</strong>{" "}
              and <strong>Directory</strong> tabs for your admin tasks.
            </p>
          </div>
        </aside>
      </div>
    );
  }

  /** Apply the current drag mode to a single cell. Skips weekends so a
   *  drag across "Mon..Sun..Mon" doesn't accidentally paint Saturday. */
  const applyDrag = (key: string, day: number): void => {
    if (!me || dragModeRef.current === null) return;
    if (isWeekend(y, m, day)) return;
    if (dragModeRef.current === "paint") {
      setCalendarCell(me, key, paintCode.value);
    } else {
      setCalendarCell(me, key, null);
    }
  };

  const startDrag = (key: string, day: number, e: MouseEvent | TouchEvent): void => {
    if (!me) return;
    if (isWeekend(y, m, day)) return;
    // Prevent text selection during a drag.
    e.preventDefault();
    const cur = s.calendar[me]?.[key];
    const mode: "paint" | "erase" = cur === paintCode.value ? "erase" : "paint";
    dragModeRef.current = mode;
    if (mode === "paint") setCalendarCell(me, key, paintCode.value);
    else setCalendarCell(me, key, null);
  };

  const continueDrag = (key: string, day: number): void => {
    applyDrag(key, day);
  };

  // Mobile day-list: one-tap apply the active paint status to a day (toggle
  // off if it already matches), mirroring the desktop click-to-paint. Reuses
  // paintCode + setCalendarCell verbatim. Weekends aren't painted — tapping
  // a weekend row opens the editor instead (for the rare note/closure case).
  const quickSet = (key: string, day: number): void => {
    if (!me) return;
    if (isWeekend(y, m, day)) {
      setModalDate(key);
      return;
    }
    const cur = s.calendar[me]?.[key];
    setCalendarCell(me, key, cur === paintCode.value ? null : paintCode.value);
  };

  // ---- Shortcut handlers ----------------------------------------------------
  const fillWeekdays = (): void => {
    if (!me) {
      toast("Pick a person to edit first");
      return;
    }
    const who = s.isAdmin ? ` for ${displayName(me)}` : "";
    if (
      !confirm(
        `Fill every weekday in ${MONTHS[m]} ${y}${who} with "${STATUS_LABEL[paintCode.value]}"?\n\n` +
          "Existing entries on weekdays will be overwritten; weekends will be left untouched.",
      )
    )
      return;
    let touched = 0;
    for (let d = 1; d <= nDays; d++) {
      if (isWeekend(y, m, d)) continue;
      setCalendarCell(me, dateKey(y, m, d), paintCode.value);
      touched++;
    }
    toast(`${touched} weekdays filled`);
  };

  const repeatLastWeek = (): void => {
    if (!me) {
      toast("Pick a person to edit first");
      return;
    }
    const cal = s.calendar[me] ?? {};
    const isCurrentMonth = today.getFullYear() === y && today.getMonth() === m;
    const lastDay = isCurrentMonth ? Math.min(today.getDate(), nDays) : nDays;
    let lastFilled = 0;
    for (let d = lastDay; d >= 1; d--) {
      if (cal[dateKey(y, m, d)]) {
        lastFilled = d;
        break;
      }
    }
    if (!lastFilled) {
      toast("No previous entries to copy");
      return;
    }
    let applied = 0;
    for (let offset = 1; offset <= 7; offset++) {
      const srcDay = lastFilled - 7 + offset;
      const dstDay = lastFilled + offset;
      if (srcDay < 1 || dstDay > nDays) continue;
      const srcCode = cal[dateKey(y, m, srcDay)];
      if (srcCode) {
        setCalendarCell(me, dateKey(y, m, dstDay), srcCode);
        applied++;
      }
    }
    if (!applied) {
      toast("Nothing to repeat — try filling a week first");
      return;
    }
    toast(`Repeated ${applied} day${applied > 1 ? "s" : ""}`);
  };

  const clearMonth = (): void => {
    if (!me) {
      toast("Pick a person to edit first");
      return;
    }
    const who = s.isAdmin ? ` for ${displayName(me)}` : "";
    if (!confirm(`Clear every entry (status, note and meeting flag) for ${MONTHS[m]} ${y}${who}?`))
      return;
    let touched = 0;
    for (let d = 1; d <= nDays; d++) {
      const k = dateKey(y, m, d);
      if (s.calendar[me]?.[k]) {
        setCalendarCell(me, k, null);
        touched++;
      }
    }
    toast(touched ? `Cleared ${touched} day${touched > 1 ? "s" : ""}` : "Already empty");
  };

  return (
    <div class="my-layout">
      <aside class="my-side">
        {/* The "act as another person" picker is super-admin only since
            migration 20 restricted plain admins from writing other
            teammates' calendars. Plain admins still see the Admin
            badge in the header and can use the Directory + Dashboard,
            but their calendar editing is locked to their own row. */}
        {s.isSuperAdmin && (
          <div class="card actas-card">
            <h3>Editing on behalf of</h3>
            <select
              value={s.actAs ?? ""}
              onChange={(e) => {
                state.value = { ...s, actAs: (e.target as HTMLSelectElement).value };
              }}
            >
              {activePeople(s.roster).map((n) => (
                <option key={n} value={n}>
                  {displayName(n)}
                </option>
              ))}
            </select>
            <div class="hint">
              All edits made in this view — paint clicks, shortcuts and the day editor — will be
              saved under the selected person's calendar.
            </div>
          </div>
        )}
        <div class="card">
          <h3>Paint mode</h3>
          <div class="paint-btns">
            {STATUS_CODES.map((c) => (
              <button
                key={c}
                class={`s-${c}${paintCode.value === c ? " active" : ""}`}
                onClick={() => (paintCode.value = c)}
                aria-pressed={paintCode.value === c}
              >
                <span class="swatch" />
                {STATUS_LABEL[c]}
              </button>
            ))}
          </div>
          <div class="hint">Pick a status, then click days to fill them. Click again to clear.</div>
        </div>
        <div class="card">
          <h3>Shortcuts</h3>
          <div class="shortcut-btns">
            <button type="button" onClick={fillWeekdays}>
              Fill all weekdays
            </button>
            <button type="button" onClick={repeatLastWeek}>
              Repeat last week
            </button>
            <button type="button" class="danger" onClick={clearMonth}>
              Clear month
            </button>
          </div>
        </div>
        <div class="card month-summary">
          <h3>Month summary</h3>
          <div class="hint">
            {me ? (
              <>
                {monthFilled} of {nDays} days filled for{" "}
                <strong>{displayName(me)}</strong>
              </>
            ) : (
              "Pick a person to see their month summary"
            )}
          </div>
          <ul class="summary-list">
            {STATUS_CODES.map((c) => (
              <li key={c} class="summary-row">
                <span class="summary-label">
                  <span class={`summary-dot s-${c}`} aria-hidden="true" />
                  {STATUS_LABEL[c]}
                </span>
                <b>{monthCounts[c]}</b>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <section class="my-main">
        <h2 class="my-title">
          <span class="my-month">{`${MONTHS[m]} ${y}`}</span>
          <span class="my-who-label">
            {s.isAdmin ? "Editing" : "Signed in as"}{" "}
            <strong>{me ? displayName(me) : "—"}</strong>
          </span>
        </h2>
        <div class="my-month-grid" role="grid">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => (
            <div key={w} class="wd-head">
              {w}
            </div>
          ))}
          {Array.from({ length: firstWd }).map((_, idx) => (
            <div key={`empty-${idx}`} class="day-cell empty" aria-hidden="true" />
          ))}
          {Array.from({ length: nDays }, (_, i) => i + 1).map((d) => {
            const we = isWeekend(y, m, d);
            const key = dateKey(y, m, d);
            const code = me ? s.calendar[me]?.[key] : undefined;
            const note = me ? s.notes[me]?.[key] : undefined;
            const closure = s.closures[key];
            const isClosure = closure !== undefined;
            const holiday = getHoliday(key);
            const isHoliday = holiday !== null;
            const isToday =
              today.getFullYear() === y && today.getMonth() === m && today.getDate() === d;
            const cls = ["day-cell"];
            if (we) cls.push("weekend");
            if (code) cls.push("filled", `s-${code}`);
            if (isToday) cls.push("today");
            if (isClosure) cls.push("closure");
            if (isHoliday) cls.push("holiday");
            const inOffice = busyByDay[d] ?? 0;
            const busyPct =
              !we && totalPeople ? Math.min(100, (inOffice / totalPeople) * 100) : 0;
            const busyCls = busynessClass(inOffice, totalPeople);

            return (
              <div
                key={d}
                class={cls.join(" ")}
                role="button"
                tabindex={0}
                onMouseDown={(e) => startDrag(key, d, e as unknown as MouseEvent)}
                onMouseEnter={() => continueDrag(key, d)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    // Keyboard fallback: single-cell toggle, no drag involved.
                    if (!me || we) return;
                    const cur = s.calendar[me]?.[key];
                    if (cur === paintCode.value) setCalendarCell(me, key, null);
                    else setCalendarCell(me, key, paintCode.value);
                  } else if (e.key === "F2") {
                    setModalDate(key);
                  }
                }}
                aria-label={`Day ${d}${code ? `, ${STATUS_LABEL[code]}` : ", no status"}${isHoliday ? `, ${holiday.name}` : ""}${isClosure ? ", office closed" : ""}`}
              >
                <span class="dnum">{d}</span>
                {!we && (
                  <span
                    class="busy-label"
                    title={`${inOffice} of ${totalPeople} colleagues in office on this day`}
                  >
                    {inOffice}
                  </span>
                )}
                <button
                  type="button"
                  class="edit-btn"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setModalDate(key);
                  }}
                  aria-label={`Edit details for day ${d}`}
                >
                  ✎
                </button>
                {code && <div class={`status-chip s-${code}`}>{STATUS_LABEL[code]}</div>}
                {note && <div class="note-text">{note}</div>}
                {isHoliday && (
                  <div class="holiday-tag" title={holiday.name}>
                    🇮🇹 {holiday.name}
                  </div>
                )}
                {isClosure && (
                  <div
                    class="closure-tag"
                    title={closure ? `Office closed — ${closure}` : "Office closed"}
                  >
                    🚫 CLOSED{closure ? ` · ${closure}` : ""}
                  </div>
                )}
                {!we && (
                  <div class="busy-bar" title={`${inOffice} of ${totalPeople} in office`}>
                    <div class={`fill ${busyCls}`} style={`width:${busyPct}%`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Mobile day-list. Rendered alongside the grid above and toggled
            purely in CSS (grid hidden ≤720px, this shown). On a phone the
            7-column grid is too cramped to tap reliably, so each day becomes
            a full-width row that opens the day editor on tap. */}
        <ul class="my-day-list" ref={dayListRef} aria-label={`Days in ${MONTHS[m]} ${y}`}>
          {Array.from({ length: nDays }, (_, i) => i + 1).map((d) => {
            const we = isWeekend(y, m, d);
            const key = dateKey(y, m, d);
            const code = me ? s.calendar[me]?.[key] : undefined;
            const note = me ? s.notes[me]?.[key] : undefined;
            const closure = s.closures[key];
            const isClosure = closure !== undefined;
            const holiday = getHoliday(key);
            const isHoliday = holiday !== null;
            const isToday =
              today.getFullYear() === y && today.getMonth() === m && today.getDate() === d;
            const inOffice = busyByDay[d] ?? 0;
            const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date(y, m, d).getDay()];
            const cls = ["day-row"];
            if (we) cls.push("weekend");
            if (code) cls.push("filled", `s-${code}`);
            if (isToday) cls.push("today");
            if (isClosure) cls.push("closure");
            if (isHoliday) cls.push("holiday");
            // Two sibling buttons under a non-button container (no nested
            // <button>): the main area one-tap quick-sets the status; the
            // trailing button opens the editor for notes/meetings.
            return (
              <li key={d}>
                <div class={cls.join(" ")}>
                  <button
                    type="button"
                    class="day-row-set"
                    onClick={() => quickSet(key, d)}
                    aria-label={
                      we
                        ? `Day ${d} ${wd}${code ? `, ${STATUS_LABEL[code]}` : ""} — open editor`
                        : `Set day ${d} ${wd} to ${STATUS_LABEL[paintCode.value]}${code ? `, currently ${STATUS_LABEL[code]}` : ""}`
                    }
                  >
                    <span class="day-row-date">
                      <span class="day-row-wd">{wd}</span>
                      <span class="day-row-num">{d}</span>
                      {isToday && <span class="day-row-today">Today</span>}
                    </span>
                    <span class="day-row-body">
                      {code ? (
                        <span class={`status-chip s-${code}`}>{STATUS_LABEL[code]}</span>
                      ) : (
                        <span class="day-row-empty">{we ? "Weekend" : "Tap to set status"}</span>
                      )}
                      {note && <span class="day-row-note">{note}</span>}
                      {isHoliday && <span class="day-row-tag">🇮🇹 {holiday.name}</span>}
                      {isClosure && (
                        <span class="day-row-tag closed">
                          🚫 Closed{closure ? ` · ${closure}` : ""}
                        </span>
                      )}
                    </span>
                  </button>
                  <button
                    type="button"
                    class="day-row-go"
                    onClick={() => setModalDate(key)}
                    aria-label={`Edit day ${d} ${wd} — note and meeting`}
                  >
                    {!we && (
                      <span class="day-row-busy" title={`${inOffice} of ${totalPeople} colleagues in office`}>
                        {inOffice}
                      </span>
                    )}
                    <span class="day-row-edit-ico" aria-hidden="true">✎</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        {/* Mobile sticky bulk-action bar (MC2). Surfaces the paint-status
            picker (so the 1-tap quick-set above applies the intended status)
            plus the existing Fill-weekdays / Repeat-last-week shortcuts in the
            thumb zone. Mobile-only (CSS-gated); the full sidebar cards stay
            below as the desktop surface + an untouched fallback. */}
        <div class="mobile-bulkbar" aria-label="Quick actions">
          <div class="mbb-pills" role="group" aria-label="Status to apply on tap">
            {STATUS_CODES.map((c) => (
              <button
                key={c}
                type="button"
                class={`mbb-pill s-${c}${paintCode.value === c ? " active" : ""}`}
                onClick={() => (paintCode.value = c)}
                aria-pressed={paintCode.value === c}
              >
                <span class="swatch" aria-hidden="true" />
                {STATUS_LABEL[c]}
              </button>
            ))}
          </div>
          <div class="mbb-actions">
            <button type="button" onClick={fillWeekdays}>Fill weekdays</button>
            <button type="button" onClick={repeatLastWeek}>Repeat last week</button>
          </div>
        </div>

        {/* Persistent legend so users don't have to remember P/T/R/M/O. */}
        <div class="status-legend" aria-label="Status colour legend">
          {STATUS_CODES.map((c) => (
            <span key={c} class="legend-item">
              <span class={`legend-swatch s-${c}`} />
              {STATUS_LABEL[c]}
            </span>
          ))}
          <span class="legend-divider" aria-hidden="true" />
          <span class="legend-item legend-busy">
            <span class="legend-swatch legend-busy-swatch" />
            <span class="legend-busy-label">N</span> &nbsp;= colleagues in office that day
          </span>
        </div>
      </section>
      {modalDate && me && (
        <DayModal date={modalDate} who={me} onClose={() => setModalDate(null)} />
      )}
    </div>
  );
}
